import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, Image as ImageIcon, Loader2, Pencil, RotateCcw, ShoppingBag, Tag, Trash2, Video } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { StatusBadge } from "./MarketListPage";

function Stat({ label, value, testId, tone = "" }) {
  return (
    <div className={`rounded-xl border border-app-border bg-white px-4 py-3 ${tone}`} data-testid={testId}>
      <div className="text-2xl font-heading font-semibold text-espresso">{value}</div>
      <div className="text-xs text-espresso/60">{label}</div>
    </div>
  );
}

function OfferRow({ l, t, onStatus, onDelete }) {
  const thumb = l.media.find((m) => m.kind === "image");
  const hasVideo = l.media.some((m) => m.kind === "video");
  const done = l.type === "sell" ? "sold" : "bought";
  return (
    <div className={`bg-white rounded-xl border p-3 space-y-2 ${l.stale ? "border-amber-300" : "border-app-border"}`} data-testid={`my-offer-${l.id}`}>
      <div className="flex gap-3">
        <Link to={`/market/listing/${l.id}`} className="w-16 h-16 rounded-lg bg-cream-2 overflow-hidden shrink-0 flex items-center justify-center text-espresso/30">
          {thumb ? <img src={`${API}/market/media/${thumb.id}`} alt="" className="w-full h-full object-cover" loading="lazy" /> : hasVideo ? <Video className="w-5 h-5" /> : <ImageIcon className="w-5 h-5" />}
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <Link to={`/market/listing/${l.id}`} className="font-semibold text-espresso truncate flex-1 hover:text-olive">{l.title}</Link>
            {l.status === "open"
              ? <span className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 font-bold uppercase tracking-wider" data-testid="listing-status-open">{t("market.status_open")}</span>
              : <StatusBadge status={l.status} t={t} />}
          </div>
          <div className="text-[11px] text-espresso/60 mt-0.5 flex flex-wrap gap-x-2 items-center">
            <span className="inline-flex items-center gap-1 font-semibold text-olive">{l.type === "sell" ? <Tag className="w-3 h-3" /> : <ShoppingBag className="w-3 h-3" />}{t(l.type === "sell" ? "market.selling" : "market.buying")}</span>
            <span>{t(`market.species_${l.species}`)}</span>
            <span>{t("market.age_days").replace("{n}", l.age_days)}</span>
            {l.stale && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold" data-testid="stale-tag">
                <Clock className="w-3 h-3" />{t("market.stale_tag")}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pt-1 border-t border-app-border/60">
        <button
          type="button"
          onClick={() => onStatus(l, l.status === "open" ? done : "open")}
          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${l.status === "open" ? "bg-red-600 text-white hover:bg-red-700" : "bg-white border border-app-border text-espresso hover:border-olive"}`}
          data-testid={`my-offer-status-${l.id}`}
        >
          {l.status === "open" ? t(`market.mark_${done}`) : <><RotateCcw className="w-3 h-3" />{t("market.mark_open")}</>}
        </button>
        <Link to={`/market/listing/${l.id}/edit`} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-white border border-app-border text-espresso hover:border-olive transition-colors" data-testid={`my-offer-edit-${l.id}`}>
          <Pencil className="w-3 h-3" />{t("common.edit")}
        </Link>
        <button type="button" onClick={() => onDelete(l)} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 transition-colors ml-auto rtl:ml-0 rtl:mr-auto" data-testid={`my-offer-delete-${l.id}`}>
          <Trash2 className="w-3 h-3" />{t("common.delete")}
        </button>
      </div>
    </div>
  );
}

export default function MarketMinePage() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/market/mine").then(({ data }) => setData(data)).catch(() => setData({ listings: [], counts: { open: 0, done: 0, stale: 0 } }));
  useEffect(() => { load(); }, []);

  const onStatus = async (l, status) => {
    const { data: upd } = await api.patch(`/market/listings/${l.id}`, { status });
    toast.success(upd.status === "open" ? t("market.reopened") : t(`market.${upd.status}`));
    load();
  };
  const onDelete = async () => {
    await api.delete(`/market/listings/${toDelete.id}`);
    toast.success(t("market.deleted"));
    setToDelete(null);
    load();
  };

  const rows = (data?.listings || []).filter((l) => filter === "all" || (filter === "open" ? l.status === "open" : filter === "stale" ? l.stale : l.status !== "open"));
  const pills = [["all", t("market.filter_all")], ["open", t("market.filter_pending")], ["done", t("market.filter_done")], ["stale", t("market.stale_tag")]];

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="market-mine-page">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/market" className="text-espresso/60 hover:text-espresso" data-testid="market-back"><ArrowLeft className="w-5 h-5 rtl:rotate-180" /></Link>
        <div className="flex-1">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-espresso">{t("market.mine")}</h1>
          <p className="text-xs text-espresso/60">{t("market.mine_sub")}</p>
        </div>
      </div>
      {data && (
        <div className="grid grid-cols-3 gap-2">
          <Stat label={t("market.filter_pending")} value={data.counts.open} testId="mine-count-open" />
          <Stat label={t("market.filter_done")} value={data.counts.done} testId="mine-count-done" />
          <Stat label={t("market.stale_tag")} value={data.counts.stale} testId="mine-count-stale" tone={data.counts.stale ? "border-amber-300 bg-amber-50" : ""} />
        </div>
      )}
      <div className="flex flex-wrap gap-1.5" data-testid="mine-filter">
        {pills.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`mine-filter-${k}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${filter === k ? "bg-olive text-white border-olive" : "bg-white text-espresso/70 border-app-border hover:border-olive"}`}>
            {label}
          </button>
        ))}
      </div>
      {data === null ? (
        <Loader2 className="w-5 h-5 animate-spin text-espresso/50" />
      ) : rows.length === 0 ? (
        <div className="text-sm text-espresso/50 bg-white rounded-xl border border-app-border p-6 text-center" data-testid="mine-empty">{t("market.mine_empty")}</div>
      ) : (
        <div className="space-y-2" data-testid="mine-rows">{rows.map((l) => <OfferRow key={l.id} l={l} t={t} onStatus={onStatus} onDelete={setToDelete} />)}</div>
      )}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("market.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("market.delete_body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-yes">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
